import { getApiBaseUrl } from "src/config/Endpoints";
import { normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";

const API_BASE_URL = getApiBaseUrl();

export interface MetaAdsDatePeriod {
  start_date: string;
  end_date: string;
}

export interface MetaAdsPaginationMeta {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

/** Shared performance metrics on campaign, ad set, and ad rows. */
export interface MetaAdsPerformanceMetrics {
  total_revenue: number;
  total_spend: number;
  impressions: number;
  clicks: number;
  cost_per_click: number;
  cost_per_purchase: number;
  ctr: number;
  roas: number;
  add_to_cart: number;
  initiate_checkout: number;
  purchases: number;
}

export interface MetaAdsKpiItem {
  id: string;
  title: string;
  value: number;
  formatted_value: string;
  previous_value: number;
  percent_change: number;
  /** Localized change string, e.g. "-7.36%" / "-٧.٣٦%". */
  percent_changeFormattedValue?: string;
}

export interface MetaAdsKpiResponse {
  period: MetaAdsDatePeriod;
  comparison_period: MetaAdsDatePeriod;
  kpis: MetaAdsKpiItem[];
}

export interface MetaAdsAdsetItem extends MetaAdsPerformanceMetrics {
  adset_id?: string;
  adset_name: string;
  platform_name?: string;
  budget_category?: string;
  daily_budget?: number;
  budget_remaining?: number;
  link_clicks?: number;
  adset_start_at?: string;
  adset_end_at?: string;
  adset_created_at?: string;
}

export interface MetaAdsCampaignItem extends MetaAdsPerformanceMetrics {
  campaign_id?: string;
  campaign_name: string;
  platform_name?: string;
  adsets?: MetaAdsAdsetItem[];
  budget_category?: string;
  daily_budget?: number;
  budget_remaining?: number;
  link_clicks?: number;
  campaign_start_at?: string;
  campaign_stop_at?: string;
  campaign_created_at?: string;
}

export interface MetaAdsAdItem extends MetaAdsPerformanceMetrics {
  ad_id?: string;
  ad_name: string;
  platform_name?: string;
  campaign_name?: string;
  daily_budget?: number;
  budget_remaining?: number;
  link_clicks?: number;
  created_at_date?: string;
  created_at_time?: string;
}

export type MetaAdsCampaignSort =
  | "campaign_name_desc"
  | "campaign_name_asc"
  | "platform_name_desc"
  | "platform_name_asc"
  | "total_revenue_desc"
  | "total_revenue_asc"
  | "total_spend_desc"
  | "total_spend_asc"
  | "impressions_desc"
  | "impressions_asc"
  | "clicks_desc"
  | "clicks_asc"
  | "cost_per_click_desc"
  | "cost_per_click_asc"
  | "cost_per_purchase_desc"
  | "cost_per_purchase_asc"
  | "ctr_desc"
  | "ctr_asc"
  | "roas_desc"
  | "roas_asc"
  | "add_to_cart_desc"
  | "add_to_cart_asc"
  | "initiate_checkout_desc"
  | "initiate_checkout_asc"
  | "purchases_desc"
  | "purchases_asc"
  | "link_clicks_desc"
  | "link_clicks_asc"
  | "campaign_start_at_desc"
  | "campaign_start_at_asc"
  | "campaign_stop_at_desc"
  | "campaign_stop_at_asc"
  | "campaign_created_at_desc"
  | "campaign_created_at_asc";

export type MetaAdsAdsetSort =
  | "adset_name_desc"
  | "adset_name_asc"
  | "platform_name_desc"
  | "platform_name_asc"
  | "total_revenue_desc"
  | "total_revenue_asc"
  | "total_spend_desc"
  | "total_spend_asc"
  | "impressions_desc"
  | "impressions_asc"
  | "clicks_desc"
  | "clicks_asc"
  | "cost_per_click_desc"
  | "cost_per_click_asc"
  | "cost_per_purchase_desc"
  | "cost_per_purchase_asc"
  | "ctr_desc"
  | "ctr_asc"
  | "roas_desc"
  | "roas_asc"
  | "add_to_cart_desc"
  | "add_to_cart_asc"
  | "initiate_checkout_desc"
  | "initiate_checkout_asc"
  | "purchases_desc"
  | "purchases_asc"
  | "link_clicks_desc"
  | "link_clicks_asc"
  | "adset_start_at_desc"
  | "adset_start_at_asc"
  | "adset_end_at_desc"
  | "adset_end_at_asc"
  | "adset_created_at_desc"
  | "adset_created_at_asc";

export type MetaAdsAdSort =
  | "ad_name_desc"
  | "ad_name_asc"
  | "total_revenue_desc"
  | "total_revenue_asc"
  | "total_spend_desc"
  | "total_spend_asc"
  | "impressions_desc"
  | "impressions_asc"
  | "clicks_desc"
  | "clicks_asc"
  | "cost_per_click_desc"
  | "cost_per_click_asc"
  | "cost_per_purchase_desc"
  | "cost_per_purchase_asc"
  | "ctr_desc"
  | "ctr_asc"
  | "roas_desc"
  | "roas_asc"
  | "add_to_cart_desc"
  | "add_to_cart_asc"
  | "initiate_checkout_desc"
  | "initiate_checkout_asc"
  | "purchases_desc"
  | "purchases_asc"
  | "link_clicks_desc"
  | "link_clicks_asc"
  | "created_at_date_desc"
  | "created_at_date_asc"
  | "created_at_time_desc"
  | "created_at_time_asc";

export interface MetaAdsPaginatedListParams {
  start_date: string;
  end_date: string;
  lang?: ApiLang | string | null;
  page?: number;
  page_size?: number;
  sort?: string | null;
  search?: string | null;
  platform?: string | null;
}

export interface MetaAdsCampaignsResponse {
  meta: MetaAdsPaginationMeta;
  campaigns: MetaAdsCampaignItem[];
}

export interface MetaAdsAdsetsResponse {
  meta: MetaAdsPaginationMeta;
  adsets: MetaAdsAdsetItem[];
}

export interface MetaAdsAdsResponse {
  meta: MetaAdsPaginationMeta;
  ads: MetaAdsAdItem[];
}

export interface MetaAdsRegionItem {
  region?: string;
  region_name?: string;
  name?: string;
  country?: string;
  country_name?: string;
  total_spend?: number;
  spend?: number;
  amount_spent?: number;
  conversions?: number;
  purchases?: number;
  roas?: number;
  ROAS?: number;
  return_on_ad_spend?: number;
  total_revenue?: number;
  revenue?: number;
  purchase_value?: number;
}

export interface MetaAdsRegionsResponse {
  regions?: MetaAdsRegionItem[];
  data?: MetaAdsRegionItem[];
}

export interface MetaAdsRoasByCampaignItem {
  campaign_name: string;
  roas: number;
}

export interface MetaAdsRoasByCampaignResponse {
  campaigns: MetaAdsRoasByCampaignItem[];
}

/** `GET .../platforms` — publisher platform comparison. */
export interface MetaAdsPlatformItem {
  platform_name: string;
  total_spend: number;
  total_purchases: number;
  impressions: number;
  ctr: number;
}

export interface MetaAdsPlatformsResponse {
  platforms: MetaAdsPlatformItem[];
}

/** `GET .../post-types` — performance by creative post type. */
export interface MetaAdsPostTypeItem {
  post_type: string;
  total_spend: number;
  purchases: number;
  impressions: number;
  ctr: number;
}

export interface MetaAdsPostTypesResponse {
  post_types: MetaAdsPostTypeItem[];
}

export interface MetaAdsAgeGenderItem {
  age_range?: string;
  age?: string;
  gender?: string;
  gender_name?: string;
  total_spend?: number;
  spend?: number;
  amount_spent?: number;
  conversions?: number;
  purchases?: number;
  roas?: number;
  ROAS?: number;
  total_roas?: number;
  return_on_ad_spend?: number;
}

export interface MetaAdsAgeGenderResponse {
  age_gender?: MetaAdsAgeGenderItem[];
  demographics?: MetaAdsAgeGenderItem[];
  data?: MetaAdsAgeGenderItem[];
}

export interface MetaAdsPerformanceTrendItem {
  date: string;
  total_spend: number;
  total_spendFormattedValue?: string;
  impressions: number;
  clicks: number;
  purchases: number;
  add_to_cart: number;
}

export interface MetaAdsPerformanceTrendsResponse {
  performance_trends: MetaAdsPerformanceTrendItem[];
  performance_trends_monthly: MetaAdsPerformanceTrendItem[];
}

/**
 * Creative / ad-level row. Backend may return either `creative_performance` or `ads`
 * (legacy route — prefer `fetchAdKPI` for the ads tab).
 */
export interface MetaAdsCreativePerformanceItem {
  campaign_name: string;
  ad_id?: string | number;
  ad_name?: string;
  adset_name?: string;
  platform_name?: string;
  post_type?: string | null;
  total_spend?: number;
  total_impressions?: number;
  impressions?: number;
  clicks?: number;
  conversions?: number;
  ctr?: number;
  cpc?: number;
  cpa?: number;
  roas?: number;
}

export interface MetaAdsCreativePerformanceResponse {
  creative_performance?: MetaAdsCreativePerformanceItem[];
  ads?: MetaAdsCreativePerformanceItem[];
}

type MetaAdsDashboardDateParams = {
  start_date: string;
  end_date: string;
  lang?: ApiLang | string | null;
};

const buildDateSearchParams = (params: MetaAdsDashboardDateParams) =>
  new URLSearchParams({
    start_date: params.start_date,
    end_date: params.end_date,
    lang: normalizeApiLang(params.lang),
  });

const appendOptionalListParams = (
  searchParams: URLSearchParams,
  params: MetaAdsPaginatedListParams
) => {
  if (params.page !== undefined) {
    searchParams.set("page", String(params.page));
  }
  if (params.page_size !== undefined) {
    searchParams.set("page_size", String(params.page_size));
  }
  if (params.sort) {
    searchParams.set("sort", params.sort);
  }
  if (params.search?.trim()) {
    searchParams.set("search", params.search.trim());
  }
  if (params.platform?.trim()) {
    searchParams.set("platform", params.platform.trim());
  }
};

const fetchMetaAdsDashboard = async <T,>(
  path: string,
  params: MetaAdsPaginatedListParams
): Promise<T> => {
  const searchParams = buildDateSearchParams(params);
  appendOptionalListParams(searchParams, params);

  const response = await fetch(
    `${API_BASE_URL}/meta-ads/dashboard/${path}?${searchParams.toString()}`,
    {
      method: "GET",
      headers: buildAuthHeaders("api_key"),
    }
  );

  return handleJsonResponse<T>(response);
};

export const metaAdsDashboardService = {
  fetchDashboardKPI: async (
    params: MetaAdsDashboardDateParams
  ): Promise<MetaAdsKpiResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/meta-ads/dashboard/kpi?${buildDateSearchParams(params).toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<MetaAdsKpiResponse>(response);
  },

  fetchCampaignKPI: async (
    params: MetaAdsPaginatedListParams
  ): Promise<MetaAdsCampaignsResponse> =>
    fetchMetaAdsDashboard<MetaAdsCampaignsResponse>("campaigns", params),

  fetchAdsetKPI: async (params: MetaAdsPaginatedListParams): Promise<MetaAdsAdsetsResponse> =>
    fetchMetaAdsDashboard<MetaAdsAdsetsResponse>("adsets", params),

  fetchAdKPI: async (params: MetaAdsPaginatedListParams): Promise<MetaAdsAdsResponse> =>
    fetchMetaAdsDashboard<MetaAdsAdsResponse>("ads", params),

  fetchRoasByCampaign: async (
    params: MetaAdsDashboardDateParams
  ): Promise<MetaAdsRoasByCampaignResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/meta-ads/dashboard/roas-by-campaign?${buildDateSearchParams(params).toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<MetaAdsRoasByCampaignResponse>(response);
  },

  fetchRegionsKPI: async (
    params: MetaAdsDashboardDateParams
  ): Promise<MetaAdsRegionsResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/meta-ads/dashboard/regions?${buildDateSearchParams(params).toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<MetaAdsRegionsResponse>(response);
  },

  fetchPlatformsKPI: async (
    params: MetaAdsDashboardDateParams
  ): Promise<MetaAdsPlatformsResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/meta-ads/dashboard/platforms?${buildDateSearchParams(params).toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<MetaAdsPlatformsResponse>(response);
  },

  fetchPostTypesKPI: async (
    params: MetaAdsDashboardDateParams
  ): Promise<MetaAdsPostTypesResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/meta-ads/dashboard/post-types?${buildDateSearchParams(params).toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<MetaAdsPostTypesResponse>(response);
  },

  fetchAgeGenderKPI: async (
    params: MetaAdsDashboardDateParams
  ): Promise<MetaAdsAgeGenderResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/meta-ads/dashboard/age-gender?${buildDateSearchParams(params).toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<MetaAdsAgeGenderResponse>(response);
  },

  fetchPerformanceTrends: async (
    params: MetaAdsDashboardDateParams
  ): Promise<MetaAdsPerformanceTrendsResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/meta-ads/dashboard/performance-trends?${buildDateSearchParams(params).toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<MetaAdsPerformanceTrendsResponse>(response);
  },

  fetchCreativePerformance: async (
    params: MetaAdsDashboardDateParams
  ): Promise<MetaAdsCreativePerformanceResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/meta-ads/dashboard/creative-performance?${buildDateSearchParams(params).toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<MetaAdsCreativePerformanceResponse>(response);
  },
};
